import { createFileRoute, Link } from '@tanstack/react-router'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { FormattedMessage, useIntl } from 'react-intl'
import { useState } from 'react'
import { sendResetPasswordEmail } from '@/services/authentication/sendResetPasswordEmail'
import { Spinner } from '@iaa-di/spinner'
import { LoadingCard } from '@/components/LoadingCard'
import { TestIds } from '@/constants/testIds'

export const Route = createFileRoute('/resetPasswordRequest')({
  component: ResetPasswordRequest,
})

function mapSendEmailError(resultMessage: string): string {
  if (resultMessage.includes('not on file')) return 'login.resetPasswordError'
  if (resultMessage.includes('not registered')) return 'login.notRegistered'
  if (resultMessage.includes('not active')) return 'login.accountNotActive'
  return 'login.error'
}

function ResetPasswordRequest() {
  useDocumentTitle('meta.title.login')
  const intl = useIntl()

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')
    setSuccessMessage('')
    setIsLoading(true)

    const { resultStatus, resultMessage } = await sendResetPasswordEmail(email)
    if (resultStatus) {
      setSuccessMessage('login.resetPasswordSuccess')
    } else {
      setErrorMessage(mapSendEmailError(resultMessage))
    }
    setIsLoading(false)
  }

  return (
    <div className="flex items-center justify-center overflow-hidden mt-2">
      <div className="w-full max-w-md space-y-8 px-4">
        {isLoading && (
          <div
            data-testid={TestIds.resetPasswordRequest.loadingContainer}
            className="pt-32 absolute left-[47%] z-[300]"
          >
            <Spinner />
          </div>
        )}
        {isLoading && (
          <div className="pt-16">
            <LoadingCard />
          </div>
        )}
        {!isLoading && (
          <>
            <Link
              data-testid={TestIds.resetPasswordRequest.backToLoginLink}
              to="/login"
              className="!text-black !underline mt-8"
            >
              <FormattedMessage id="login.backToLogin" />
            </Link>

            <h1 className="block text-sm font-small mb-4 mt-8">
              <FormattedMessage id="login.resetPassword" />
            </h1>
            <hr className="border-0 h-2 w-1/3 bg-red-600" />

            {successMessage && !isLoading && (
              <div
                data-testid={TestIds.resetPasswordRequest.successMessage}
                className="rounded-md bg-green-50 p-4 text-sm text-green-800"
              >
                <FormattedMessage id="login.passwordEmailSuccess" />
              </div>
            )}

            {!successMessage && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    <FormattedMessage id="login.email" />
                  </label>
                  <input
                    data-testid={TestIds.resetPasswordRequest.emailInput}
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder={intl.formatMessage({ id: 'login.email' })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  />
                </div>

                {errorMessage && (
                  <div
                    data-testid={TestIds.resetPasswordRequest.errorMessage}
                    role="alert"
                    className="rounded bg-orange-500 p-4 text-sm font-semibold text-white"
                  >
                    <FormattedMessage id={errorMessage} />
                  </div>
                )}

                <button
                  data-testid={TestIds.resetPasswordRequest.submitButton}
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FormattedMessage id="login.submit" />
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}
